import React, { useState, useEffect } from 'react';
import { employeeAPI, establishmentAPI, leaveAPI, leaveConfigAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import { Building2, Users, Calendar, Check, AlertCircle, Loader2, Save, Clock, CheckCircle, XCircle, ChevronDown, FileText } from 'lucide-react';
import DateInput from './DateInput';
import EstablishmentCard from './EstablishmentCard';
import './LeaveAbsentRecord.css';

const CURRENT_YEAR = new Date().getFullYear();

const STATUS_MAP = {
  approved:  { label: 'Approved',  cls: 'lr-st-approved',  Icon: CheckCircle },
  rejected:  { label: 'Rejected',  cls: 'lr-st-rejected',  Icon: XCircle },
  pending:   { label: 'Pending',   cls: 'lr-st-pending',   Icon: Clock },
};

const TYPE_LABELS = {
  annual: 'Annual', earned: 'Earned', sick: 'Sick', casual: 'Casual',
  maternity: 'Maternity', paternity: 'Paternity', emergency: 'Emergency',
  bereavement: 'Bereavement', study: 'Study', unpaid: 'Unpaid', other: 'Other',
  SL: 'Sick', EL: 'Earned', CL: 'Casual', Absent: 'Absent',
};

const fmtDate = (d) => {
  if (!d) return '-';
  const s = d.split('T')[0];
  const [y, m, day] = s.split('-');
  return `${day}/${m}/${y}`;
};

const LeaveAbsentRecord = () => {
  const { user } = useAuth();
  const isMember = user?.role === 'Member';
  const lockedEstId = useActiveEstId();

  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [employees, setEmployees] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [leaveData, setLeaveData] = useState({});
  const [leaveRecords, setLeaveRecords] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEstablishment(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lockedEstId && !isMember) fetchEstablishments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isMember) {
      fetchOwnRecord();
    } else if (selectedEstablishment) {
      fetchEmployees();
    }
  }, [selectedEstablishment]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeaveRecords = async (employeeList) => {
    const records = {};
    await Promise.all(
      employeeList.map(async (emp) => {
        try {
          const res = await leaveAPI.getByEmployee(emp.employee_id);
          const all = (res.leaves || [])
            .filter(r => {
              // Parse YYYY-MM-DD prefix directly — new Date() treats it as
              // UTC midnight and getFullYear() can return the prior year.
              const yr = r.start_date ? parseInt(String(r.start_date).slice(0, 4), 10) : 0;
              return yr === CURRENT_YEAR;
            })
            .sort((a, b) => String(b.start_date || '').localeCompare(String(a.start_date || '')));
          records[emp.employee_id] = all;
        } catch {
          records[emp.employee_id] = [];
        }
      })
    );
    setLeaveRecords(prev => ({ ...prev, ...records }));
  };

  const fetchEstablishments = async () => {
    try {
      const response = await establishmentAPI.getAll();
      setEstablishments(response.establishments || []);
    } catch {
      showMessage('Failed to load organizations', 'error');
    }
  };

  const fetchOwnRecord = async () => {
    setLoading(true);
    try {
      const emp = await employeeAPI.getMe();
      const employeeList = emp ? [emp] : [];
      const employeesWithLeaves = await Promise.all(
        employeeList.map(async (e) => {
          try {
            const lb = await leaveConfigAPI.getBalance(e.employee_id);
            return {
              ...e,
              leaveBalance: { sl_balance: lb.sl_balance || 0, el_balance: lb.el_balance || 0, cl_balance: lb.cl_balance || 0 },
              isOnProbation: lb.is_on_probation,
              leavesAvailable: lb.leaves_available,
            };
          } catch {
            return { ...e, leaveBalance: { sl_balance: 0, el_balance: 0, cl_balance: 0 }, isOnProbation: false, leavesAvailable: false };
          }
        })
      );
      setEmployees(employeesWithLeaves);
      if (employeesWithLeaves.length) setSelectedEmpId(employeesWithLeaves[0].employee_id);
      const init = {};
      employeesWithLeaves.forEach(e => { init[e.employee_id] = { type: '', notes: '' }; });
      setLeaveData(init);
      fetchLeaveRecords(employeesWithLeaves);
    } catch {
      showMessage('Failed to load your record', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await employeeAPI.getByEstablishment(selectedEstablishment);
      const employeeList = response.employees || [];
      const employeesWithLeaves = await Promise.all(
        employeeList.map(async (emp) => {
          try {
            const lb = await leaveConfigAPI.getBalance(emp.employee_id);
            return {
              ...emp,
              leaveBalance: { sl_balance: lb.sl_balance || 0, el_balance: lb.el_balance || 0, cl_balance: lb.cl_balance || 0 },
              isOnProbation: lb.is_on_probation,
              leavesAvailable: lb.leaves_available,
            };
          } catch {
            return { ...emp, leaveBalance: { sl_balance: 0, el_balance: 0, cl_balance: 0 }, isOnProbation: false, leavesAvailable: false };
          }
        })
      );
      setEmployees(employeesWithLeaves);
      setSelectedEmpId('');
      const init = {};
      employeesWithLeaves.forEach(emp => { init[emp.employee_id] = { type: '', notes: '' }; });
      setLeaveData(init);
      fetchLeaveRecords(employeesWithLeaves);
    } catch {
      showMessage('Failed to load employees', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveTypeChange = (employeeId, type) => {
    setLeaveData(prev => ({
      ...prev,
      [employeeId]: { ...prev[employeeId], type: prev[employeeId]?.type === type ? '' : type },
    }));
  };

  const handleSaveAll = async () => {
    const recordsToSave = Object.entries(leaveData)
      .filter(([_, data]) => data.type)
      .map(([employeeId, data]) => ({ employeeId, leaveType: data.type, date: selectedDate, notes: data.notes }));
    if (recordsToSave.length === 0) { showMessage('Please select leave/absent for at least one employee', 'warning'); return; }
    setSaving(true);
    try {
      await leaveAPI.recordLeaveAbsent({ establishmentId: selectedEstablishment, records: recordsToSave });
      notifySavedAndReload(`Successfully recorded leave/absent for ${recordsToSave.length} employee(s)`);
      setTimeout(() => { isMember ? fetchOwnRecord() : fetchEmployees(); }, 800);
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Failed to save leave records', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const getLeaveIcon = (type) => {
    switch (type) { case 'SL': return '🏥'; case 'EL': return '🌴'; case 'CL': return '📅'; case 'Absent': return '❌'; default: return ''; }
  };

  const selectedEmp = employees.find(e => String(e.employee_id) === String(selectedEmpId));
  const empRecords = selectedEmpId ? (leaveRecords[selectedEmpId] || []) : [];

  return (
    <div className="lar-container">
      {/* Header */}
      <div className="lar-header">
        <div>
          <h1 className="lar-title"><Calendar size={24} /> Leave / Absent Record</h1>
          <p className="lar-subtitle">Record daily leave and absence &middot; {CURRENT_YEAR}</p>
        </div>
      </div>

      {/* Establishment Selector */}
      {!lockedEstId && !isMember && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEstablishment}
          onChange={(e) => setSelectedEstablishment(e.target.value)}
        />
      )}

      {/* Message */}
      {message.text && (
        <div className={`lar-toast lar-toast-${message.type}`}>
          <AlertCircle size={16} /> {message.text}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="lar-loading">
          <Loader2 className="lar-spin" size={36} />
          <p>Loading...</p>
        </div>
      )}

      {/* Empty States */}
      {!loading && !isMember && !selectedEstablishment && (
        <div className="lar-empty"><Building2 size={48} /><h3>Select an Organization</h3><p>Choose an organization to manage leave records</p></div>
      )}
      {!loading && (selectedEstablishment || isMember) && employees.length === 0 && !loading && (
        <div className="lar-empty"><Users size={48} /><h3>No Employees Found</h3></div>
      )}

      {/* Main Content */}
      {!loading && employees.length > 0 && (
        <div className="lar-main">
          {/* Top Bar — Employee selector + Date + Record buttons */}
          <div className="lar-toolbar">
            {/* Employee selector (admin) or info (member) */}
            {!isMember && (
              <div className="lar-tool-field">
                <label className="lar-tool-label"><Users size={14} /> Employee</label>
                <div className="lar-select-wrap">
                  <select className="lar-select" value={selectedEmpId} onChange={e => setSelectedEmpId(e.target.value)}>
                    <option value="">-- Select employee --</option>
                    {employees.map(emp => (
                      <option key={emp.employee_id} value={emp.employee_id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_id})
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="lar-select-chevron" />
                </div>
              </div>
            )}

            <div className="lar-tool-field">
              <label className="lar-tool-label"><Calendar size={14} /> Date</label>
              <DateInput value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="lar-date-input" />
            </div>
          </div>

          {/* Selected Employee Panel */}
          {selectedEmp && (
            <>
              {/* Employee Hero + Balance Cards */}
              <div className="lar-emp-panel">
                <div className="lar-emp-hero">
                  <div className="lar-emp-avatar">
                    {(selectedEmp.first_name?.[0] || '').toUpperCase()}{(selectedEmp.last_name?.[0] || '').toUpperCase()}
                  </div>
                  <div className="lar-emp-info">
                    <h2 className="lar-emp-name">
                      {selectedEmp.first_name} {selectedEmp.last_name}
                      {selectedEmp.isOnProbation && <span className="lar-badge-prob">Probation</span>}
                    </h2>
                    <span className="lar-emp-meta">{selectedEmp.employee_id} &middot; {selectedEmp.department || 'N/A'} &middot; {selectedEmp.position || ''}</span>
                  </div>
                </div>

                {/* Balance Cards */}
                <div className="lar-balance-row">
                  <div className="lar-bal-card lar-bal-sl">
                    <div className="lar-bal-icon">🏥</div>
                    <div className="lar-bal-body">
                      <div className="lar-bal-num">{selectedEmp.leaveBalance?.sl_balance ?? 0}</div>
                      <div className="lar-bal-label">Sick Leave</div>
                    </div>
                  </div>
                  <div className="lar-bal-card lar-bal-el">
                    <div className="lar-bal-icon">🌴</div>
                    <div className="lar-bal-body">
                      <div className="lar-bal-num">{selectedEmp.leaveBalance?.el_balance ?? 0}</div>
                      <div className="lar-bal-label">Earned Leave</div>
                    </div>
                  </div>
                  <div className="lar-bal-card lar-bal-cl">
                    <div className="lar-bal-icon">📅</div>
                    <div className="lar-bal-body">
                      <div className="lar-bal-num">{selectedEmp.leaveBalance?.cl_balance ?? 0}</div>
                      <div className="lar-bal-label">Casual Leave</div>
                    </div>
                  </div>
                </div>

                {/* Quick Record Row */}
                <div className="lar-record-row">
                  <span className="lar-record-label">Record for {fmtDate(selectedDate)}:</span>
                  <div className="lar-record-btns">
                    {['SL', 'EL', 'CL', 'Absent'].map(type => {
                      const isSelected = leaveData[selectedEmp.employee_id]?.type === type;
                      const bal = selectedEmp.leaveBalance?.[`${type.toLowerCase()}_balance`] || 0;
                      const isDisabled = type !== 'Absent' && bal <= 0;
                      return (
                        <button
                          key={type}
                          onClick={() => !isDisabled && handleLeaveTypeChange(selectedEmp.employee_id, type)}
                          disabled={isDisabled}
                          className={`lar-rec-btn ${isSelected ? 'lar-rec-btn-active' : ''} ${isDisabled ? 'lar-rec-btn-disabled' : ''}`}
                        >
                          <span>{getLeaveIcon(type)}</span>
                          <span>{type}</span>
                          {isSelected && <Check size={14} />}
                        </button>
                      );
                    })}
                  </div>
                  {leaveData[selectedEmp.employee_id]?.type && (
                    <div className="lar-record-extra">
                      <input
                        type="text"
                        placeholder="Notes (optional)"
                        value={leaveData[selectedEmp.employee_id]?.notes || ''}
                        onChange={(e) => setLeaveData(prev => ({
                          ...prev,
                          [selectedEmp.employee_id]: { ...prev[selectedEmp.employee_id], notes: e.target.value },
                        }))}
                        className="lar-notes-input"
                      />
                      <button className="lar-save-btn" onClick={handleSaveAll} disabled={saving}>
                        {saving ? <><Loader2 size={14} className="lar-spin" /> Saving...</> : <><Save size={14} /> Save</>}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Leave Records Table */}
              <div className="lar-table-card">
                <div className="lar-table-header">
                  <h3 className="lar-table-title"><FileText size={16} /> Leave Records &mdash; {CURRENT_YEAR}</h3>
                  <span className="lar-table-count">{empRecords.length} record{empRecords.length !== 1 ? 's' : ''}</span>
                </div>

                {empRecords.length === 0 ? (
                  <div className="lar-table-empty">
                    <Calendar size={36} />
                    <p>No leave records for {CURRENT_YEAR}</p>
                  </div>
                ) : (
                  <div className="lar-table-wrap">
                    <table className="lar-table">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Leave Type</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Days</th>
                          <th>Applied On</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empRecords.map(rec => {
                          const st = STATUS_MAP[rec.status] || STATUS_MAP.pending;
                          const StIcon = st.Icon;
                          return (
                            <tr key={rec.leave_id}>
                              <td>
                                <span className={`lar-status-badge ${st.cls}`}>
                                  <StIcon size={12} /> {st.label}
                                </span>
                              </td>
                              <td>
                                <span className={`lar-type-badge lar-type-${rec.leave_type}`}>
                                  {TYPE_LABELS[rec.leave_type] || rec.leave_type}
                                </span>
                              </td>
                              <td className="lar-td-date">{fmtDate(rec.start_date)}</td>
                              <td className="lar-td-date">{fmtDate(rec.end_date)}</td>
                              <td className="lar-td-days">{rec.days_count}</td>
                              <td className="lar-td-date">{fmtDate(rec.applied_date)}</td>
                              <td className="lar-td-reason">{rec.reason || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Prompt to select employee (admin only) */}
          {!isMember && !selectedEmpId && (
            <div className="lar-empty" style={{ marginTop: '1.5rem' }}>
              <Users size={48} />
              <h3>Select an Employee</h3>
              <p>Choose an employee above to view their leave balance and records</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeaveAbsentRecord;
